import React, { useEffect } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Paper,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useDispatch, useSelector } from "../../../store";
import TileBase from "./TileBase";
import TileTable from "./TileTable";
import theme from "../../../theme";
import TileDataStack from "./TileStack";
import { ExpandMore } from "@mui/icons-material";
import {
  getTable,
  getTileDetail,
  updateTableSearchParams,
} from "../../../slices/tile";

interface Props {}

const TileDetailView: React.FC<Props> = (props) => {
  const { tile, tileTable, tableSearchParams } = useSelector(
    (store) => store.tiles
  );

  const dispatch = useDispatch();

  useEffect(() => {
    const getTileData = async () => {
      if (!tile?.id) return;

      await dispatch(getTileDetail(tile?.id!));

      if (tile.id !== tableSearchParams.id) {
        dispatch(
          updateTableSearchParams({
            id: tile.id,
            pageNumber: 1,
            pageSize: 10,
            orderBy: "",
          })
        );
      } else {
        await dispatch(
          getTable({ search: { ...tableSearchParams, id: tile.id } })
        );
      }
    };

    getTileData();

    const interval = setInterval(getTileData, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [dispatch, tile?.id, tableSearchParams]);

  const md = useMediaQuery(theme.breakpoints.up("md"));
  const lg = useMediaQuery(theme.breakpoints.up("lg"));

  const clipPath = `polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)`;

  const TileBox = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        padding={2}
        flex={1}
        minWidth={0}
      >
        <Box
          display="flex"
          flexDirection="row"
          sx={{
            width: "100%",
            aspectRatio: 0.89,
            backgroundColor: "#000",
            position: "relative",
            clipPath,
            pointerEvents: "auto",
          }}
          marginY={md ? 2 : 0}
        >
          <Box display="flex" flexDirection="column" height="100%" width="100%">
            <TileBase tile={tile} detail={true} />
          </Box>
        </Box>
      </Box>
    );
  };

  const changePage = (pageNumber?: number, pageSize?: number) => {
    dispatch(
      updateTableSearchParams({ ...tableSearchParams, pageNumber, pageSize })
    );
  };

  return (
    <Box
      display="flex"
      flexDirection={lg ? "row" : "column"}
      justifyContent="space-between"
      marginTop={2}
      paddingY={md ? 3 : 2}
      height="100%"
      width="100%"
      minHeight={0}
    >
      {lg ? (
        <TileBox />
      ) : (
        <Accordion
          disableGutters
          sx={{
            textAlign: "center",
            marginX: "15px",
            borderRadius: "5px",
          }}
          defaultExpanded={false}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography fontWeight="bold">View Tile</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TileBox />
          </AccordionDetails>
        </Accordion>
      )}
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        padding={2}
        flex={lg ? 2 : undefined}
        minWidth={0}
        width={lg ? undefined : "100%"}
      >
        <Paper
          sx={{
            width: "100%",
            height: "100%",
            marginBottom: 5,
            padding: 2,
            display: "flex",
            flexDirection: "row",
            minHeight: 0,
          }}
        >
          <Box
            display={{ xs: "none", md: "flex" }}
            flexDirection="column"
            justifyContent="center"
            marginY={2}
            width="100%"
          >
            <TileTable
              id={tile?.id!}
              tileTable={tileTable!}
              changePage={changePage}
            />
          </Box>
          <Box
            display={{ xs: "flex", md: "none" }}
            flexDirection="row"
            justifyContent="center"
            marginY={2}
            width="100%"
          >
            <TileDataStack id={tile?.id!} tileTable={tileTable!} />
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default TileDetailView;
